import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getUser, logout, shortAddress } from "../lib/auth";
import { getNetworkLabel, isFaucetEnabled } from "../lib/runtimeConfig";

/**
 * Identity, the pages opened now and then, and sign-out. Disconnect is a menu
 * item, not a click on the chip, so a stray click on your address keeps the session.
 */
export function AccountMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const address = user?.kind === "wallet" ? user.address : undefined;
  const network = getNetworkLabel();

  // Outside click and Escape both close it, or it covers what the user reaches for.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // A panel left over a new page reads as a rendering bug.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate("/login");
  };

  /** Wallet sessions are identified by address; demo sessions by name. */
  const label = address !== undefined ? shortAddress(address) : (user?.name ?? "Account");

  return (
    <div className="account-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={open ? "account-trigger account-trigger-open" : "account-trigger"}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
      >
        {address !== undefined && <span className="wallet-dot" aria-hidden="true" />}
        <span className="account-trigger-label">{label}</span>
      </button>

      {open && (
        <div className="account-panel" role="menu">
          {address !== undefined && (
            <div className="account-panel-head">
              <span className="account-panel-caption">Connected wallet</span>
              <span className="account-panel-address" title={address}>
                {shortAddress(address)}
              </span>
              {network !== null && <span className="chain-badge">{network}</span>}
            </div>
          )}

          <Link className="account-panel-item" role="menuitem" to="/history">
            Transaction history
          </Link>
          {isFaucetEnabled() && (
            <Link className="account-panel-item" role="menuitem" to="/faucet">
              Faucet
            </Link>
          )}

          <button
            type="button"
            className="account-panel-item account-panel-danger"
            role="menuitem"
            onClick={handleLogout}
          >
            {address !== undefined ? "Disconnect" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}
